package co.maytes.standupbot.dashboard.features.releases

import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.FocusRing
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StyleScope

internal object FeedClasses {
    const val PAGE = "sb-rel-page"
    const val BAR = "sb-rel-bar"
    const val FILTERS = "sb-rel-filters"
    const val FILTERS_ROW = "sb-rel-filters-row"
    const val SUMMARY_ROW = "sb-rel-summary-row"
    const val SUMMARY = "sb-rel-summary"
    const val VIEW_CONTROLS = "sb-rel-view-controls"
    const val FILTER_TOGGLE = "sb-rel-filter-toggle"
    const val SEARCH = "sb-rel-search"
    const val FEED = "sb-rel-feed"
    const val DAY = "sb-rel-day"
    const val ITEM = "sb-rel-item"
    const val ROW = "sb-rel-row"
    const val META = "sb-rel-meta"
    const val ICON = "sb-rel-icon"
    const val TITLE = "sb-rel-title"
    const val CHEVRON = "sb-rel-chevron"
    const val AUTHOR = "sb-rel-author"
    const val TIME = "sb-rel-time"
    const val DETAIL = "sb-rel-detail"
    const val CARD = "sb-rel-card"
    const val CARDS = "sb-rel-cards"
    const val CHIP = "sb-rel-chip"
    const val POPOVER = "sb-rel-popover"
    const val OPTION = "sb-rel-option"
    const val GROUP_HEAD = "sb-rel-group-head"
    const val BAR_FILL = "sb-rel-skeleton-bar"
}

private const val NAV_TOP_BAR_HEIGHT = "60px"
private const val MOBILE_NAV_MAX = 959
private const val PHONE_MAX = 639
private const val NARROW_MAX = 1279

private val mobileNavMedia = CSSMediaQuery.Raw("(max-width: ${MOBILE_NAV_MAX}px)")
private val phoneMedia = CSSMediaQuery.Raw("(max-width: ${PHONE_MAX}px)")
private val narrowMedia = CSSMediaQuery.Raw("(max-width: ${NARROW_MAX}px)")
private val reducedMotionMedia = CSSMediaQuery.Raw(Motion.REDUCED_MOTION_QUERY)

private val rowColumns =
    "${IconSize.lg} minmax(0, 1fr) minmax(0, 8rem) minmax(0, 11rem) minmax(0, 12rem) 6.5rem ${IconSize.md}"
private val narrowRowColumns = "${IconSize.lg} minmax(0, 1fr) minmax(0, 8rem) minmax(0, 11rem) 6.5rem ${IconSize.md}"
private val phoneRowColumns = "${IconSize.lg} minmax(0, 1fr) ${IconSize.md}"
private val chromeTransition = Motion.transition(Motion.DUR_FAST, "background-color", "border-color", "color")

private class RuleSheet {
    val entries = linkedMapOf<String, MutableList<Pair<CSSMediaQuery?, StyleScope.() -> Unit>>>()

    fun rule(
        selector: String,
        media: CSSMediaQuery? = null,
        build: StyleScope.() -> Unit,
    ) {
        entries.getOrPut(selector) { mutableListOf() }.add(media to build)
    }

    fun register(ctx: InitSilkContext) {
        entries.forEach { (selector, parts) ->
            ctx.stylesheet.registerStyle(selector) {
                base {
                    Modifier.styleModifier { parts.filter { it.first == null }.forEach { (_, build) -> build() } }
                }
                parts.filter { it.first != null }.forEach { (media, build) ->
                    cssRule(media!!, null) { Modifier.styleModifier(build) }
                }
            }
        }
    }
}

private fun StyleScope.focusInset() {
    property("outline", FocusRing.OUTLINE)
    property("outline-offset", "calc(${FocusRing.OFFSET} * -1)")
}

private fun StyleScope.resetButton() {
    property("appearance", "none")
    property("background-color", "transparent")
    property("border", "0")
    property("margin", "0")
    property("font", "inherit")
    property("color", "inherit")
    property("text-align", "left")
    property("cursor", "pointer")
}

private fun RuleSheet.layoutRules() {
    rule(".${FeedClasses.PAGE}") { property("--sb-rel-top", "0px") }
    rule(".${FeedClasses.PAGE}", mobileNavMedia) { property("--sb-rel-top", NAV_TOP_BAR_HEIGHT) }
    rule(".${FeedClasses.BAR}") {
        property("position", "sticky")
        property("top", "var(--sb-rel-top, 0px)")
        property("z-index", "${ZIndex.sticky}")
        property("background-color", Palette.background.cssRgb())
        property("padding", "${Spacing.sm} 0")
        property("border-bottom", "1px solid ${Palette.borderSubtle.cssRgb()}")
    }
    rule(".${FeedClasses.FILTERS}") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "${Spacing.sm}")
    }
    rule(".${FeedClasses.FILTERS_ROW}") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "${Spacing.sm}")
    }
    rule(".${FeedClasses.SUMMARY_ROW}") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("gap", "${Spacing.xs} ${Spacing.md}")
        property("margin-top", "${Spacing.sm}")
    }
    rule(".${FeedClasses.SUMMARY}") {
        property("display", "flex")
        property("flex", "1 1 auto")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "${Spacing.xs}")
        property("min-width", "0")
    }
    rule(".${FeedClasses.VIEW_CONTROLS}") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.xs}")
    }
    rule(".${FeedClasses.FILTER_TOGGLE}") { property("display", "none") }
    rule(".${FeedClasses.SEARCH}") {
        property("flex", "1 1 16rem")
        property("min-width", "0")
        property("max-width", "28rem")
    }
    rule(".${FeedClasses.CARDS}", phoneMedia) {
        property("display", "flex")
        property("overflow-x", "auto")
        property("scroll-snap-type", "x mandatory")
        property("overscroll-behavior-x", "contain")
    }
    rule(".${FeedClasses.CARD}", phoneMedia) {
        property("flex", "0 0 70%")
        property("scroll-snap-align", "start")
    }
    rule(".${FeedClasses.FILTER_TOGGLE}", phoneMedia) { property("display", "inline-flex") }
    rule(".${FeedClasses.FILTERS}", phoneMedia) { property("display", "none") }
    rule(".${FeedClasses.FILTERS}[data-open=\"true\"]", phoneMedia) {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "stretch")
        property("width", "100%")
    }
    rule(".${FeedClasses.SEARCH}", phoneMedia) {
        property("max-width", "none")
        property("flex", "1 1 0")
    }
    rule(".${FeedClasses.CARDS}") {
        property("display", "grid")
        property("grid-template-columns", "repeat(auto-fit, minmax(11rem, 1fr))")
        property("gap", "${Spacing.sm}")
    }
}

private fun RuleSheet.rowRules() {
    rule(".${FeedClasses.DAY}") {
        property("position", "sticky")
        property("top", "calc(var(--sb-rel-top, 0px) + var(--sb-rel-bar-h, 0px))")
        property("z-index", "${ZIndex.sticky - 1}")
        property("display", "flex")
        property("justify-content", "space-between")
        property("align-items", "baseline")
        property("background-color", Palette.background.cssRgb())
        property("padding", "${Spacing.sm} ${Spacing.sm} ${Spacing.xs}")
        property("border-bottom", "1px solid ${Palette.border.cssRgb()}")
    }
    rule(".${FeedClasses.FEED}") { property("width", "100%") }
    rule(".${FeedClasses.ITEM}") { property("border-bottom", "1px solid ${Palette.borderSubtle.cssRgb()}") }
    rule(".${FeedClasses.ROW}") {
        resetButton()
        property("display", "grid")
        property("grid-template-columns", rowColumns)
        property("align-items", "center")
        property("column-gap", "${Spacing.sm}")
        property("width", "100%")
        property("box-sizing", "border-box")
        property("padding", "${Spacing.sm} ${Spacing.sm}")
        property("border-radius", "${Radius.md}")
        property("transition", chromeTransition)
    }
    rule(".${FeedClasses.FEED}[data-density=\"compact\"] .${FeedClasses.ROW}") {
        property("padding", "${Spacing.xxs} ${Spacing.sm}")
    }
    rule(".${FeedClasses.ROW}:hover") { property("background-color", Palette.surfaceHover.cssRgb()) }
    rule(".${FeedClasses.ROW}:focus-visible") { focusInset() }
    rule(".${FeedClasses.META}") { property("display", "contents") }
    rule(".${FeedClasses.TIME}") {
        property("justify-self", "end")
        property("text-align", "right")
        property("white-space", "nowrap")
        property("font-variant-numeric", "tabular-nums")
    }
    rule(".${FeedClasses.ROW}", narrowMedia) { property("grid-template-columns", narrowRowColumns) }
    rule(".${FeedClasses.AUTHOR}", narrowMedia) { property("display", "none") }
    rule(".${FeedClasses.ROW}", phoneMedia) {
        property("grid-template-columns", phoneRowColumns)
        property("grid-template-areas", "'icon title chevron' '. meta meta'")
        property("row-gap", "${Spacing.xxs}")
    }
    rule(".${FeedClasses.META}", phoneMedia) {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("column-gap", "${Spacing.sm}")
        property("grid-area", "meta")
        property("min-width", "0")
    }
    rule(".${FeedClasses.ICON}", phoneMedia) { property("grid-area", "icon") }
    rule(".${FeedClasses.TITLE}", phoneMedia) { property("grid-area", "title") }
    rule(".${FeedClasses.CHEVRON}", phoneMedia) { property("grid-area", "chevron") }
    rule(".${FeedClasses.AUTHOR}", phoneMedia) { property("display", "inline") }
    rule(".${FeedClasses.TIME}", phoneMedia) { property("margin-left", "auto") }
    rule(".${FeedClasses.DETAIL}") {
        property("padding", "${Spacing.xxs} ${Spacing.md} ${Spacing.md} calc(${IconSize.lg} + ${Spacing.sm} + ${Spacing.sm})")
    }
    rule(".${FeedClasses.DETAIL}", phoneMedia) {
        property("padding", "${Spacing.xxs} ${Spacing.sm} ${Spacing.md} ${Spacing.sm}")
    }
}

private fun RuleSheet.controlRules() {
    rule(".${FeedClasses.GROUP_HEAD}") {
        resetButton()
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.sm}")
        property("width", "100%")
        property("box-sizing", "border-box")
        property("padding", "${Spacing.sm} ${Spacing.sm}")
        property("border-radius", "${Radius.md}")
        property("transition", chromeTransition)
    }
    rule(".${FeedClasses.GROUP_HEAD}:hover") { property("background-color", Palette.surfaceHover.cssRgb()) }
    rule(".${FeedClasses.GROUP_HEAD}:focus-visible") { focusInset() }
    rule(".${FeedClasses.CARD}") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.xxs}")
        property("min-width", "0")
        property("padding", "${Spacing.sm} ${Spacing.md}")
        property("text-decoration", "none")
        property("color", "inherit")
        property("background-color", Palette.surface.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", "${Radius.lg}")
        property("transition", chromeTransition)
    }
    rule(".${FeedClasses.CARD}:hover") {
        property("background-color", Palette.surfaceHover.cssRgb())
        property("border-color", Palette.accentBorder.cssRgb())
    }
    rule(".${FeedClasses.CARD}:focus-visible") {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", FocusRing.OFFSET)
    }
    rule(".${FeedClasses.CHIP}") {
        resetButton()
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "${Spacing.xxs}")
        property("max-width", "100%")
        property("padding", "${Spacing.xxs} ${Spacing.xs} ${Spacing.xxs} ${Spacing.sm}")
        property("border-radius", "${Radius.full}")
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("transition", chromeTransition)
    }
    rule(".${FeedClasses.CHIP}:hover") { property("background-color", Palette.surfaceHover.cssRgb()) }
    rule(".${FeedClasses.CHIP}:focus-visible") {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", FocusRing.OFFSET)
    }
    rule(".${FeedClasses.POPOVER}") {
        property("box-sizing", "border-box")
        property("min-width", "min(20rem, calc(100vw - 2rem))")
        property("padding", "${Spacing.xs}")
        property("background-color", Palette.surface.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", "${Radius.lg}")
        property("box-shadow", Shadow.MD)
    }
    rule(".${FeedClasses.OPTION}") {
        resetButton()
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.sm}")
        property("width", "100%")
        property("box-sizing", "border-box")
        property("padding", "${Spacing.xs} ${Spacing.sm}")
        property("border-radius", "${Radius.md}")
        property("transition", chromeTransition)
    }
    rule(".${FeedClasses.OPTION}:hover") { property("background-color", Palette.surfaceHover.cssRgb()) }
    rule(".${FeedClasses.OPTION}:focus-visible") { focusInset() }
    rule(".${FeedClasses.BAR_FILL}") {
        property("background-color", Palette.borderSubtle.cssRgb())
        property("border-radius", "${Radius.inner}")
        property("height", "${Spacing.md}")
    }
    listOf(FeedClasses.ROW, FeedClasses.GROUP_HEAD, FeedClasses.CARD, FeedClasses.CHIP, FeedClasses.OPTION).forEach {
        rule(".$it", reducedMotionMedia) { property("transition", "none") }
    }
}

private fun RuleSheet.markdownRules() {
    val root = ".${MarkdownClasses.ROOT}"
    rule(root) {
        property("display", "grid")
        property("gap", "${Spacing.sm}")
        property("max-width", "72ch")
        property("overflow-wrap", "anywhere")
        property("font-size", "${Typography.bodySmall.size}")
        property("line-height", "1.6")
        property("color", Palette.textSecondary.cssRgb())
    }
    rule("$root p, $root h3, $root h4, $root h5, $root ul, $root ol, $root hr, $root pre") { property("margin", "0") }
    rule("$root h3, $root h4, $root h5") {
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${Typography.h4.weight}")
        property("line-height", "1.4")
        property("color", Palette.textPrimary.cssRgb())
        property("padding-top", "${Spacing.xs}")
    }
    rule("$root h3") { property("font-size", "${Typography.body.size}") }
    rule("$root ul, $root ol") {
        property("padding-left", "${Spacing.lg}")
        property("display", "grid")
        property("gap", "${Spacing.xxs}")
    }
    rule("$root li > ul, $root li > ol") { property("margin-top", "${Spacing.xxs}") }
    rule("$root li::marker") { property("color", Palette.textTertiary.cssRgb()) }
    rule("$root b") {
        property("font-weight", "${Typography.h4.weight}")
        property("color", Palette.textPrimary.cssRgb())
    }
    rule("$root hr") {
        property("border", "0")
        property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
        property("width", "100%")
    }
    rule(".${MarkdownClasses.CODE}") {
        property("font-family", Typography.MONO_STACK.joinToString(", "))
        property("font-size", "${Typography.caption.size}")
        property("padding", "0.1em 0.4em")
        property("border-radius", "${Radius.inner}")
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".${MarkdownClasses.PRE}") {
        property("overflow", "auto")
        property("padding", "${Spacing.sm} ${Spacing.md}")
        property("border-radius", "${Radius.md}")
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }
    rule(".${MarkdownClasses.PRE} code") {
        property("font-family", Typography.MONO_STACK.joinToString(", "))
        property("font-size", "${Typography.caption.size}")
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".${MarkdownClasses.QUOTE}") {
        property("padding-left", "${Spacing.md}")
        property("border-left", "2px solid ${Palette.border.cssRgb()}")
        property("color", Palette.textTertiary.cssRgb())
    }
    rule(".${MarkdownClasses.LINK}") {
        property("color", Palette.textPrimary.cssRgb())
        property("text-decoration", "underline")
        property("text-decoration-color", Palette.border.cssRgb())
        property("text-underline-offset", "0.2em")
        property("transition", chromeTransition)
    }
    rule(".${MarkdownClasses.LINK}:hover") { property("text-decoration-color", Palette.textPrimary.cssRgb()) }
}

@InitSilk
fun initReleaseFeedStyles(ctx: InitSilkContext) {
    val sheet = RuleSheet()
    sheet.layoutRules()
    sheet.rowRules()
    sheet.controlRules()
    sheet.markdownRules()
    sheet.register(ctx)
}
